import { useEffect } from 'react';

/**
 * Publishes the real visible viewport height as a CSS custom property `--vvh`
 * (in px) on <html>.
 *
 * Why: `100vh` on mobile Chrome/Safari is the *largest* viewport (URL bar
 * hidden). When the URL bar or keyboard is showing, or the OS font/display
 * scale is 125–150%, a `max-height: 94vh` modal is taller than what the user
 * can actually see and its footer buttons fall off-screen.
 *
 * Usage in CSS (fallback keeps the old behavior on browsers without
 * visualViewport, so nothing changes for them):
 *   max-height: calc(var(--vvh, 100vh) * 0.94);
 */
export default function useVisualViewportHeight() {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    const root = document.documentElement;
    let frame = 0;

    const update = () => {
      frame = 0;
      root.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('orientationchange', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('orientationchange', schedule);
      root.style.removeProperty('--vvh');
    };
  }, []);
}
